import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ToastContainer } from "react-toastify";

/* ================= FORGOT PASSWORD ================= */
const ForCode = lazy(() =>
  import("./pages/forgot-password/forgot-passcode/forcode")
);
const ForPass = lazy(() =>
  import("./pages/forgot-password/forgot-password-mail/forgtopass.jsx")
);
const NewPass = lazy(() =>
  import("./pages/forgot-password/new-password/newpass")
);
const PassCh = lazy(() =>
  import("./pages/forgot-password/password-changed/passch")
);

/* ================= HOME ================= */
const Home = lazy(() => import("./pages/home/home"));

/* ================= DOCTOR AUTH ================= */
const DocSignup = lazy(() =>
  import("./pages/doctor/doctor-signup/docsignup")
);
const DocSignin = lazy(() =>
  import("./pages/doctor/doctor-signin/docsignin")
);

/* ================= PATIENT AUTH ================= */
const PatientSignup = lazy(() =>
  import("./pages/patient/patient-signup/patientsignup")
);
const PatientSignin = lazy(() =>
  import("./pages/patient/patient-signin/patientsignin")
);

/* ================= DOCTORS ================= */
const Doctors = lazy(() =>
  import("./pages/home/doctorsearching/doctorsearch.jsx")
);

/* ================= OTHER PAGES ================= */
const HealthBlog = lazy(() =>
  import("./pages/health-blog/healthblog.jsx")
);
const ContactUs = lazy(() =>
  import("./pages/contactus/contact")
);
const PrivacyPolicy = lazy(() =>
  import("./pages/privacypolicy/privacypolicy.jsx")
);
// Added Terms component here
const Terms = lazy(() =>
  import("./pages/termsandcondition/terms.jsx")
);

/* ================= DOCTOR DASHBOARD ================= */
const DoctorDashboardPage = lazy(() =>
  import("./doctor-dashboard-pages/DoctorDashboardPage")
);
const PatientDoc = lazy(() =>
  import("./doctor-dashboard-pages/patientdoc")
);
const ManageBookingDoc = lazy(() =>
  import("./doctor-dashboard-pages/managebookingdoc")
);
const DocSetting = lazy(() =>
  import("./doctor-dashboard-pages/docsetting")
);

/* ================= PATIENT DASHBOARD ================= */
const PatientDashboard = lazy(() =>
  import("./patient-dashboard-pages/patientdashboard")
);
const FindDoctors = lazy(() =>
  import("./patient-dashboard-pages/finddoctors")
);
const Bookings = lazy(() =>
  import("./patient-dashboard-pages/bookings")
);
const Profile = lazy(() =>
  import("./patient-dashboard-pages/profile")
);

/* ================= ADMIN DASHBOARD ================= */
const AdminDashboardPage = lazy(() =>
  import("./admin-dashboard-pages/admindashboardpage")
);
const AdminDoctor = lazy(() =>
  import("./admin-dashboard-pages/admindoctor")
);
const AdminPatient = lazy(() =>
  import("./admin-dashboard-pages/adminpatient")
);
const AdminReport = lazy(() =>
  import("./admin-dashboard-pages/adminreport")
);
const AdminSettings = lazy(() =>
  import("./admin-dashboard-pages/adminsettings")
);

/* ================= VIDEO CALL ================= */
const VideoCall = lazy(() =>
  import("./components/video-call")
);

/* ================= PROTECTED DOCTOR ROUTE ================= */
const DoctorRoute = ({ children }) => {
  const doctor = JSON.parse(localStorage.getItem("ilaj360-doctor"));

  if (!doctor) {
    return <Navigate to="/doctor-signin" replace />;
  }

  return children;
};

/* ================= PROTECTED ADMIN ROUTE ================= */
const AdminRoute = ({ children }) => {
  return children;
};

/* ================= LOADING ================= */
const PageLoader = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    Loading...
  </div>
);

/* ================= APP ================= */
function App() {
  return (
    <Router>
      <ToastContainer position="top-right" />

      <Suspense fallback={<PageLoader />}>
        <Routes>

          {/* ================= VIDEO CALL ================= */}
          <Route
            path="/video-call/:appointmentId"
            element={<VideoCall />}
          />

          {/* ================= PUBLIC PAGES ================= */}
          <Route
            path="/"
            element={
              window.location.hostname === "admin.doctorgo360.com" ? (
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              ) : (
                <Home />
              )
            }
          />

          {/* ================= DOCTOR AUTH ================= */}
          <Route
            path="/doctor-signin"
            element={<DocSignin />}
          />

          <Route
            path="/doctor-signup"
            element={<DocSignup />}
          />

          {/* ================= PATIENT AUTH ================= */}
          <Route
            path="/patient-signin"
            element={<PatientSignin />}
          />

          <Route
            path="/patient-signup"
            element={<PatientSignup />}
          />

          {/* ================= DOCTORS ================= */}
          <Route
            path="/doctors"
            element={<Doctors />}
          />

          {/* Backward-compatible redirect */}
          <Route
            path="/speciality/dentist"
            element={<Navigate to="/doctors" replace />}
          />

          {/* ================= BLOG ================= */}
          <Route
            path="/healthblog"
            element={<HealthBlog />}
          />

          {/* Backward-compatible redirect */}
          <Route
            path="/health-blog/healthblog"
            element={
              <Navigate to="/healthblog" replace />
            }
          />

          {/* ================= CONTACT ================= */}
          <Route
            path="/contact"
            element={<ContactUs />}
          />

          {/* ================= PRIVACY POLICY ================= */}
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicy />}
          />

          {/* ================= TERMS AND CONDITIONS ================= */}
          {/* Added Terms route here */}
          <Route
            path="/terms"
            element={<Terms />}
          />

          {/* ================= FORGOT PASSWORD ================= */}
          <Route
            path="/forgot-password-mail"
            element={<ForPass />}
          />

          <Route
            path="/forgot-password-code"
            element={<ForCode />}
          />

          <Route
            path="/forgot-password-new"
            element={<NewPass />}
          />

          <Route
            path="/forgot-password-success"
            element={<PassCh />}
          />

          {/* ================= DOCTOR DASHBOARD ================= */}
          <Route
            path="/doctor-dashboard"
            element={
              <DoctorRoute>
                <DoctorDashboardPage />
              </DoctorRoute>
            }
          />

          <Route
            path="/patients"
            element={
              <DoctorRoute>
                <PatientDoc />
              </DoctorRoute>
            }
          />

          <Route
            path="/manage-booking-doc"
            element={
              <DoctorRoute>
                <ManageBookingDoc />
              </DoctorRoute>
            }
          />

          <Route
            path="/docsetting"
            element={
              <DoctorRoute>
                <DocSetting />
              </DoctorRoute>
            }
          />

          {/* ================= ADMIN DASHBOARD ================= */}
          <Route
            path="/admin-dashboard"
            element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            }
          />

          <Route
            path="/admin-doctors"
            element={
              <AdminRoute>
                <AdminDoctor />
              </AdminRoute>
            }
          />

          <Route
            path="/admin-patients"
            element={
              <AdminRoute>
                <AdminPatient />
              </AdminRoute>
            }
          />

          <Route
            path="/admin-reports"
            element={
              <AdminRoute>
                <AdminReport />
              </AdminRoute>
            }
          />

          <Route
            path="/admin-settings"
            element={
              <AdminRoute>
                <AdminSettings />
              </AdminRoute>
            }
          />

          {/* 
          <Route
            path="/admin-documents"
            element={<AdminDocument />}
          />
          */}

          {/* ================= PATIENT DASHBOARD ================= */}
          <Route
            path="/patient-dashboard"
            element={<PatientDashboard />}
          />

          <Route
            path="/patient-find-doctors"
            element={<FindDoctors />}
          />

          <Route
            path="/patient-bookings"
            element={<Bookings />}
          />

          <Route
            path="/patient-profile"
            element={<Profile />}
          />

          {/* ================= FALLBACK ================= */}
          <Route
            path="*"
            element={<Navigate to="/" replace />}
          />

        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;